import { useParams } from "@tanstack/react-router";
import type { Formula, Regra } from "@vetbolso/contracts";
import {
  bsa,
  choque,
  cri,
  dataBR,
  doseCalc,
  espMatch,
  faixa,
  fluidos,
  fmt,
  type KChoque,
  type KFluidos,
  type KRer,
  type KTransfusao,
  nomeEspecie,
  pequeno,
  rer,
  transfusao,
} from "@vetbolso/core";
import { Icone, type NomeIcone } from "@vetbolso/ui";
import { type ReactNode, useState } from "react";
import {
  Alertas,
  Busca,
  Cabeca,
  CampoNumero,
  CampoPeso,
  Carimbo,
  Cartao,
  combina,
  Grande,
  Grupo,
  MOSTRAR_FONTES,
  Pares,
  Refs,
  Rodape,
  refsUnicas,
  Secao,
  SemResultado,
  SpPill,
  type Tom,
  textoPeso,
  Vazio,
} from "../comp.tsx";
import { useApp, useBase } from "../estado.tsx";
import { ItensRegra } from "../folhas.tsx";
import { TelaBulario } from "./bulario.tsx";
import { TelaConta } from "./conta.tsx";
import { TelaMapas } from "./mapas-rotas.tsx";

const SUBS: Record<string, () => ReactNode> = {
  bulario: TelaBulario,
  mapas: TelaMapas,
  emergencia: Emergencia,
  calculadoras: Calculadoras,
  vacinas: Vacinas,
  mdr1: Mdr1,
  referencia: SinaisVitais,
  dor: Dor,
  farmacia: () => <TelaRegras titulo="Posso aviar?" sub="Receita veterinária na farmácia" publico="farmacia" />,
  agro: () => <TelaRegras titulo="Exige receita?" sub="Balcão agropecuário" publico="agro" />,
  programas: Programas,
  carencia: Carencia,
  plantas: Plantas,
  estudo: Estudo,
  ...(MOSTRAR_FONTES ? { fontes: Fontes } : {}),
  conta: TelaConta,
};

export function TelaSub() {
  const { sub } = useParams({ strict: false }) as { sub: string };
  const C = SUBS[sub];
  return C ? <C /> : <Cabeca titulo="Em breve" />;
}

const TOM_PUBLICO: Record<string, [Tom, NomeIcone]> = {
  farmacia: ["inter", "store"],
  agro: ["campo", "store"],
  campo: ["campo", "route"],
};

function CartoesRegra({ publico, especie }: { publico: Regra["publico"]; especie?: string }) {
  const base = useBase();
  const rs = base.regras.filter((r) => r.publico === publico && (!especie || espMatch(r, especie)));
  if (!rs.length) return null;
  const [tom, icone] = TOM_PUBLICO[publico] ?? ["dose", "file"];
  return (
    <>
      {rs.map((r) => (
        <Secao key={r.id} titulo={r.titulo} icone={icone} tom={tom} n={r.itens?.length}>
          {r.resumo ? <p className="resumo">{r.resumo}</p> : null}
          <ItensRegra itens={r.itens} />
          {r.nota ? <p className="dica">{r.nota}</p> : null}
          <Refs refs={r.refs} compacto />
        </Secao>
      ))}
    </>
  );
}

function TelaRegras({ titulo, sub, publico }: { titulo: string; sub: string; publico: Regra["publico"] }) {
  const tem = useBase().regras.some((r) => r.publico === publico);
  return (
    <>
      <Cabeca titulo={titulo} sub={sub} />
      {tem ? <CartoesRegra publico={publico} /> : <div className="empty">Sem regras conferidas.</div>}
      <Carimbo />
    </>
  );
}

function Programas() {
  const base = useBase();
  const { especie: e } = useApp();
  const tem = base.regras.some((r) => r.publico === "campo" && espMatch(r, e));
  return (
    <>
      <Cabeca titulo="Programas sanitários" sub={`Defesa agropecuária · ${nomeEspecie(e)}`} />
      {tem ? (
        <CartoesRegra publico="campo" especie={e} />
      ) : (
        <div className="empty">Sem regras conferidas para {nomeEspecie(e).toLowerCase()}.</div>
      )}
      <Carimbo />
    </>
  );
}

function Emergencia() {
  const base = useBase();
  const { especie: e, peso, unPeso } = useApp();
  if (!pequeno(e))
    return (
      <>
        <Cabeca titulo="Emergência" sub={nomeEspecie(e)} />
        <Vazio>
          As diretrizes RECOVER cobrem cães e gatos. Para {nomeEspecie(e).toLowerCase()}, nenhuma tabela de RCP foi conferida em fonte
          aberta nesta versão.
        </Vazio>
      </>
    );
  const em = base.emergencia;
  const drogas = (em?.drogas ?? []).filter((d) => espMatch(d, e));
  return (
    <>
      <Cabeca titulo="Emergência · RCP" sub={`${nomeEspecie(e)} · RECOVER`} />
      <CampoPeso id="peso-rcp" />
      <Alertas lista={em?.alertas} />
      <Secao titulo={`Fármacos para ${textoPeso(peso, unPeso)}`} icone="syringe" tom="contra" n={drogas.length}>
        <ul className="itens">
          {drogas.map((d) => {
            const c = doseCalc(d, peso);
            const vol = c?.vols[0];
            return (
              <li key={d.id}>
                <div className="linha-top">
                  <b className="linha-nome">{d.farmaco}</b>
                  <span className="linha-v">{c ? `${faixa(c.lo, c.hi, 3)} ${c.base}` : d.texto}</span>
                </div>
                <span className="dica">
                  <span>
                    {[d.uso, `${faixa(d.min ?? 0, d.max, 3)} ${d.un}`, d.via].filter(Boolean).join(" · ")}
                    {vol && c ? (
                      <>
                        {" · "}
                        <span className="mono">
                          {faixa(vol.lo, vol.hi, 2)} mL{c.suf}
                        </span>{" "}
                        de {vol.rotulo}
                      </>
                    ) : null}
                  </span>
                </span>
                <Refs refs={d.refs} compacto />
              </li>
            );
          })}
        </ul>
      </Secao>
      <Secao titulo="Suporte básico" icone="pulse" tom="inter">
        <dl className="kv">
          {(em?.rcp ?? []).map((r) => (
            <div key={r.rotulo} style={{ display: "contents" }}>
              <dt>{r.rotulo}</dt>
              <dd>
                {r.valor}
                <Refs refs={r.refs} compacto />
              </dd>
            </div>
          ))}
        </dl>
      </Secao>
      <Carimbo />
    </>
  );
}

// ---------- calculadoras ----------

function Calculadoras() {
  const base = useBase();
  const { especie: e, calc, setCalc } = useApp();
  const calcs = base.formulas.filter((f) => f.calc);
  const f = base.formulas.find((x) => x.id === calc);
  return (
    <>
      <Cabeca titulo="Calculadoras" sub={nomeEspecie(e)} />
      {/* biome-ignore lint/a11y/useSemanticElements: grupo de botões de alternância, como no protótipo */}
      <div className="chips" role="group" aria-label="Calculadora">
        {calcs.map((c) => (
          <button key={c.id} type="button" className="chip" aria-pressed={calc === c.id} onClick={() => setCalc(c.id)}>
            {c.curto}
          </button>
        ))}
      </div>
      {f ? <FormCalc key={f.id} f={f} /> : <div className="empty">Selecione uma calculadora.</div>}
    </>
  );
}

function FormCalc({ f }: { f: Formula }) {
  const { especie: e, peso } = useApp();
  const [v, setV] = useState<Record<string, number | null>>({
    desid: 0,
    perdas: 0,
    htPac: 12,
    htAlvo: 20,
    htDoador: 40,
    dose: 0.1,
    conc: 10,
  });
  const K = f.constantes ? f.constantes[e] : null;
  if (f.constantes && K == null)
    return (
      <>
        <div className="omit">
          A fórmula “{f.nome}” não tem constante conferida para {nomeEspecie(e).toLowerCase()}. Campo omitido em vez de estimado.
        </div>
        <Refs refs={f.refs} />
      </>
    );
  const campo = (k: string, rotulo: string, positivo = true) => (
    <CampoNumero
      key={k}
      id={`ci-${k}`}
      rotulo={rotulo}
      valor={v[k] ?? null}
      aoMudar={(n) => setV((x) => ({ ...x, [k]: n }))}
      positivo={positivo}
    />
  );
  const pesoCampo = <CampoPeso key="peso" id="ci-peso" rotulo="Peso" solto />;
  const campos: Record<string, ReactNode[]> = {
    fluidos: [pesoCampo, campo("desid", "Desidratação (%)", false), campo("perdas", "Perdas contínuas (mL/dia)", false)],
    rer: [pesoCampo],
    transfusao: [pesoCampo, campo("htPac", "Ht do paciente (%)"), campo("htAlvo", "Ht desejado (%)"), campo("htDoador", "Ht da bolsa (%)")],
    bsa: [pesoCampo],
    cri: [pesoCampo, campo("dose", "Dose (mg/kg/h)"), campo("conc", "Concentração (mg/mL)")],
    choque: [pesoCampo],
  };
  return (
    <>
      <Secao titulo={f.nome} icone="calc" tom="dose">
        <div className="fields">{campos[f.id]}</div>
        <ResultadoCalc id={f.id} K={K} p={peso} v={v} />
        <p className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
          {f.expr}
          {typeof K === "number" ? ` · K ${nomeEspecie(e).toLowerCase()} = ${fmt(K, 1)}` : ""}
        </p>
        <Refs refs={f.refs} compacto />
        {f.nota ? <p className="dica">{f.nota}</p> : null}
      </Secao>
      <Carimbo />
    </>
  );
}

function ResultadoCalc({ id, K, p, v }: { id: string; K: unknown; p: number; v: Record<string, number | null> }) {
  if (!p || p <= 0) return <p className="sub">Informe o peso.</p>;
  if (id === "fluidos") {
    const r = fluidos(K as KFluidos, p, v.desid ?? 0, v.perdas ?? 0);
    return (
      <>
        <div className="tscroll">
          <table className="t">
            <thead>
              <tr>
                <th>Manutenção</th>
                <th>mL/dia</th>
                <th>mL/h</th>
              </tr>
            </thead>
            <tbody>
              {r.manutencao.map((m) => (
                <tr key={m.rotulo}>
                  <td>{m.rotulo}</td>
                  <td className="n">{fmt(m.mlDia, 0)}</td>
                  <td className="n">{fmt(m.mlH, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid2">
          <Grande v={`${fmt(r.deficitMl, 0)} mL`} l="déficit a repor" />
          <Grande cls="alt" v={`${fmt(r.perdasMl, 0)} mL`} l="perdas contínuas em 24 h" />
        </div>
      </>
    );
  }
  if (id === "rer") {
    const r = rer(K as KRer, p);
    return (
      <>
        <Grande v={`${fmt(r.kcal, 0)} kcal/dia`} l="energia de repouso (RER)" />
        <div className="tscroll">
          <table className="t">
            <thead>
              <tr>
                <th>Manutenção (MER)</th>
                <th>Fator</th>
                <th>kcal/dia</th>
              </tr>
            </thead>
            <tbody>
              {r.mer.map((m) => (
                <tr key={m.rotulo}>
                  <td>{m.rotulo}</td>
                  <td className="n">{faixa(m.a, m.b, 1)}</td>
                  <td className="n">{faixa(m.lo, m.hi, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }
  if (id === "transfusao") {
    const k = K as KTransfusao;
    const r = transfusao(k, p, v.htPac ?? 0, v.htAlvo ?? 0, v.htDoador ?? 0);
    if (!r) return <p className="sub">O Ht desejado precisa ser maior que o do paciente.</p>;
    return (
      <>
        <Grande v={`${faixa(r.lo, r.hi, 0)} mL`} l={`concentrado de hemácias, volume sanguíneo de ${k.vsMin}–${k.vsMax} mL/kg`} />
        {r.regraMl != null ? (
          <Grande cls="alt" v={`${fmt(r.regraMl, 0)} mL`} l={`regra validada em cães: ${fmt(k.regra, 1)} mL/kg por ponto de Ht`} />
        ) : null}
      </>
    );
  }
  if (id === "bsa") return <Grande v={`${fmt(bsa(K as number, p), 3)} m²`} l="área de superfície corporal" />;
  if (id === "cri") {
    if (!v.dose || !v.conc) return <p className="sub">Informe dose e concentração.</p>;
    return <Grande v={`${fmt(cri(p, v.dose, v.conc), 2)} mL/h`} l="velocidade da bomba" />;
  }
  if (id === "choque") {
    const r = choque(K as KChoque, p);
    return <Grande v={`${faixa(r.lo, r.hi, 0)} mL`} l={`bolus de cristaloide isotônico em ${r.tempo}, depois reavaliar`} />;
  }
  return null;
}

// ---------- consulta ----------

const TIPO_VACINA: Record<string, string> = {
  core: "essencial",
  "nao-core": "não essencial",
  obrigatoria: "obrigatória por lei",
  programa: "programa oficial",
};

function Vacinas() {
  const base = useBase();
  const { especie: e } = useApp();
  const vs = base.vacinas.filter((v) => espMatch(v, e));
  return (
    <>
      <Cabeca titulo="Vacinas" sub={nomeEspecie(e)} />
      {vs.length ? (
        vs.map((v) => (
          <Secao
            key={v.id}
            titulo={v.titulo}
            icone="shield"
            tom={v.tipo === "obrigatoria" ? "aviso" : "ind"}
            extra={
              v.tipo ? <span className={`etq ${v.tipo === "obrigatoria" ? "atencao" : "bom"}`}>{TIPO_VACINA[v.tipo] ?? v.tipo}</span> : null
            }
          >
            <ul className="itens">
              {v.itens.map((i) => (
                <li key={i}>
                  <p>{i}</p>
                </li>
              ))}
            </ul>
            <Refs refs={v.refs} compacto />
          </Secao>
        ))
      ) : (
        <Vazio icone="shield">Nenhum protocolo conferido para {nomeEspecie(e).toLowerCase()}.</Vazio>
      )}
      <Carimbo />
    </>
  );
}

const CATS_MDR1: Record<string, [string, Tom, NomeIcone]> = {
  evitar: ["Evitar", "contra", "proibido"],
  consultar: ["Ajustar dose com o laboratório", "aviso", "alert"],
  seguro: ["Testado em cães MDR1, dose de bula", "ind", "check"],
};

function Mdr1() {
  const m = useBase().mdr1;
  const { especie } = useApp();
  const [q, setQ] = useState("");
  if (!m) return <Cabeca titulo="MDR1 (ABCB1)" />;
  if (especie === "gato" && m.gato)
    return (
      <>
        <Cabeca titulo="MDR1 (ABCB1)" sub="Gatos" />
        <p className="resumo">{m.gato.texto}</p>
        <Rodape refs={m.gato.refs} titulo="Fonte" />
      </>
    );
  return (
    <>
      <Cabeca titulo="MDR1 (ABCB1)" sub="Cães com a mutação" />
      <Busca valor={q} aoMudar={setQ} placeholder="Fármaco" rotulo="Buscar fármaco" />
      {q ? null : <p className="resumo">{m.resumo}</p>}
      {q && !m.farmacos.some((f) => combina(q, f.nome, f.nota)) ? <SemResultado termo={q} /> : null}
      {Object.entries(CATS_MDR1).map(([c, [rot, tom, icone]]) => {
        const fs = m.farmacos.filter((f) => f.cat === c && combina(q, f.nome, f.nota));
        return fs.length ? (
          <Secao key={c} titulo={rot} icone={icone} tom={tom} n={fs.length}>
            <ul className="itens">
              {fs.map((f) => (
                <li key={f.nome}>
                  <b className="linha-nome">{f.nome}</b>
                  {f.nota ? <p>{f.nota}</p> : null}
                  <Refs refs={f.refs} compacto />
                </li>
              ))}
            </ul>
          </Secao>
        ) : null;
      })}
      {m.racas.length && !q ? (
        <Secao titulo="Frequência da mutação por raça" icone="dna" tom="inter" n={m.racas.length}>
          <div className="tscroll">
            <table className="t">
              <thead>
                <tr>
                  <th>Raça</th>
                  <th>WSU (EUA)</th>
                  <th>Brasil</th>
                </tr>
              </thead>
              <tbody>
                {m.racas.map((r) => (
                  <tr key={r.raca}>
                    <td>{r.raca}</td>
                    <td className="n">{r.wsu}</td>
                    <td className="n">{r.br}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="dica">A WSU não diz se o número é frequência do alelo ou de portadores. O estudo brasileiro mede o alelo.</p>
          <Refs refs={m.racasRefs} compacto />
        </Secao>
      ) : null}
      <Rodape refs={m.refs} titulo="Fonte geral" />
    </>
  );
}

function SinaisVitais() {
  const base = useBase();
  const { especie: e } = useApp();
  const rs = base.referencia;
  return (
    <>
      <Cabeca titulo="Sinais vitais" sub="Adultos em repouso" />
      <Secao titulo="Temperatura, FC e FR" icone="thermo" tom="dose">
        <div className="tscroll">
          <table className="t">
            <thead>
              <tr>
                <th>Espécie</th>
                <th>Temp. (°C)</th>
                <th>FC (bpm)</th>
                <th>FR (mpm)</th>
              </tr>
            </thead>
            <tbody>
              {rs.map((r) => (
                <tr key={r.especie + (r.rotulo ?? "")} style={r.especie === e ? { background: "var(--primary-soft)" } : undefined}>
                  <td>
                    <b>{nomeEspecie(r.especie)}</b>
                    {r.rotulo ? (
                      <>
                        <br />
                        <span className="sub">{r.rotulo}</span>
                      </>
                    ) : null}
                  </td>
                  <td className="n">{r.temp || "—"}</td>
                  <td className="n">{r.fc || "—"}</td>
                  <td className="n">{r.fr || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Refs refs={refsUnicas(rs.map((r) => r.refs))} compacto />
      </Secao>
      {pequeno(e) && base.laboratorio.length ? (
        <Secao titulo={`Laboratório · ${nomeEspecie(e)}`} icone="drop" tom="inter" n={base.laboratorio.length}>
          <div className="tscroll">
            <table className="t">
              <thead>
                <tr>
                  <th>Analito</th>
                  <th>Intervalo</th>
                </tr>
              </thead>
              <tbody>
                {base.laboratorio.map((l) => (
                  <tr key={l.analito}>
                    <td>{l.analito}</td>
                    <td className="n">
                      {(e === "cao" ? l.cao : l.gato) || "—"} {l.un}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Refs refs={refsUnicas(base.laboratorio.map((r) => r.refs))} compacto />
        </Secao>
      ) : null}
      <p className="dica">Intervalos variam com idade, porte, raça e laboratório. Use os do laboratório que fez o exame.</p>
      <Carimbo />
    </>
  );
}

function Dor() {
  const base = useBase();
  const { especie: e } = useApp();
  const es = base.escalas.filter((x) => espMatch(x, e));
  return (
    <>
      <Cabeca titulo="Escalas de dor" sub={nomeEspecie(e)} />
      <p className="dica">Ponto de corte a partir do qual a diretriz indica analgesia de resgate.</p>
      {es.length ? (
        <ul className="cartoes">
          {es.map((x) => (
            <Cartao
              key={x.id}
              icone="pulse"
              tom="efeitos"
              titulo={x.nome}
              sub={[x.faixa, x.nota].filter(Boolean).join(" · ")}
              valor={
                <>
                  <span>resgate</span>
                  <b>{x.corte}</b>
                </>
              }
            >
              <Refs refs={x.refs} compacto />
            </Cartao>
          ))}
        </ul>
      ) : (
        <Vazio icone="pulse">Sem escala de dor conferida para {nomeEspecie(e).toLowerCase()}.</Vazio>
      )}
      <Carimbo />
    </>
  );
}

function Carencia() {
  const base = useBase();
  const { especie: e, abrir } = useApp();
  const [q, setQ] = useState("");
  const prods = base.produtos.filter((p) => p.especies.includes(e as never));
  const lista = (prods.length ? prods : base.produtos).filter((p) => combina(q, p.produto, p.farmaco, p.fabricante));
  const c = base.campo?.carencia;
  return (
    <>
      <Cabeca titulo="Carência por produto" sub={prods.length ? nomeEspecie(e) : "Todos os produtos"} />
      <Busca valor={q} aoMudar={setQ} placeholder="Produto, princípio ativo ou fabricante" rotulo="Buscar produto" />
      {c && !q ? <Alertas lista={[{ n: "info", t: c.texto, refs: c.refs }]} /> : null}
      {lista.length ? (
        <Grupo titulo="Produtos registrados no MAPA" n={lista.length}>
          {lista.map((p) => (
            <Cartao
              key={p.id}
              icone="caixa"
              tom="campo"
              titulo={p.produto}
              sub={[p.farmaco, (p.fabricante || "").split(" ")[0]].filter(Boolean).join(" · ")}
              aoTocar={() => abrir({ t: "prod", id: p.id })}
            >
              <Pares
                itens={[
                  ["Carne", p.carenciaCarne ?? "—"],
                  ["Leite", p.carenciaLeite ?? "—"],
                ]}
              />
              {p.secundaria ? <span className="item-pe">Confira a bula do produto.</span> : null}
            </Cartao>
          ))}
        </Grupo>
      ) : q ? (
        <SemResultado termo={q} />
      ) : (
        <Vazio icone="caixa">Nenhum produto conferido.</Vazio>
      )}
      <Carimbo />
    </>
  );
}

/** Dose letal curta para o cartão: "0,6 g/kg" de "0,6 g/kg de folhas frescas, dose única…". */
const dlCurta = (dl?: string) => dl?.match(/^[\d.,–-]+\s*[^\s,;]+/)?.[0];

function Plantas() {
  const base = useBase();
  const { especie: e, abrir } = useApp();
  const [q, setQ] = useState("");
  const ps = base.plantas.filter((p) => espMatch(p, e));
  const todas = ps.length ? ps : base.plantas;
  const lista = todas.filter((p) => combina(q, p.nome, p.cientifico, p.efeito));
  const pan = base.campo?.panorama;
  return (
    <>
      <Cabeca titulo="Plantas tóxicas" sub={ps.length ? nomeEspecie(e) : "Grandes animais"} />
      <Busca valor={q} aoMudar={setQ} placeholder="Nome popular, científico ou efeito" rotulo="Buscar planta" />
      {pan && !q ? <Alertas lista={[{ n: "info", t: pan.texto, refs: pan.refs }]} /> : null}
      {lista.length ? (
        <Grupo titulo={q ? "Resultados" : "Plantas conferidas"} n={lista.length}>
          {lista.map((p) => {
            const curta = dlCurta(p.dl);
            return (
              <Cartao
                key={p.id}
                icone="leaf"
                tom="campo"
                titulo={p.nome}
                pills={
                  <>
                    {p.cientifico ? <i className="item-cientifico">{p.cientifico}</i> : null}
                    {p.especies.map((x) => (
                      <SpPill key={x} id={x} />
                    ))}
                  </>
                }
                sub={p.efeito}
                aoTocar={() => abrir({ t: "planta", id: p.id })}
              >
                {p.dl || p.alertas?.length ? (
                  <span className="item-chips">
                    {p.dl ? <span className="etq mal">{curta ? `dose letal ${curta}` : "dose letal descrita"}</span> : null}
                    {p.alertas?.length ? (
                      <span className="etq atencao">{p.alertas.length === 1 ? "1 alerta" : `${p.alertas.length} alertas`}</span>
                    ) : null}
                  </span>
                ) : null}
              </Cartao>
            );
          })}
        </Grupo>
      ) : q ? (
        <SemResultado termo={q} />
      ) : (
        <Vazio icone="leaf">Nenhuma planta conferida.</Vazio>
      )}
      <Carimbo />
    </>
  );
}

function Estudo() {
  const qs = useBase().questoes;
  const [s, setS] = useState({ i: 0, ans: null as number | null, ok: 0, feitas: 0 });
  if (!qs.length)
    return (
      <>
        <Cabeca titulo="Questões" />
        <div className="empty">Sem questões.</div>
      </>
    );
  const q = qs[s.i % qs.length]!;
  const a = s.ans;
  return (
    <>
      <Cabeca titulo="Questões" sub={`${s.ok} de ${s.feitas} certas nesta sessão`} />
      <div className="card quiz">
        <span className="eyebrow">
          Questão {(s.i % qs.length) + 1} de {qs.length}
        </span>
        <h3 style={{ fontSize: 16 }}>{q.q}</h3>
        {q.opcoes.map((o, i) => (
          <button
            key={o}
            type="button"
            className={`opt${a != null ? (i === q.correta ? " right" : i === a ? " wrong" : "") : ""}`}
            disabled={a != null}
            onClick={() => setS((x) => ({ ...x, ans: i, feitas: x.feitas + 1, ok: x.ok + (i === q.correta ? 1 : 0) }))}
          >
            {o}
          </button>
        ))}
        {a != null ? (
          <>
            <div className={`alert ${a === q.correta ? "info" : "warning"}`}>
              <Icone nome={a === q.correta ? "check" : "info"} className="sm" />
              <div>
                <p>{q.explicacao}</p>
                <Refs refs={q.refs} />
              </div>
            </div>
            <button type="button" className="btn" onClick={() => setS((x) => ({ ...x, i: x.i + 1, ans: null }))}>
              Próxima
            </button>
          </>
        ) : null}
      </div>
    </>
  );
}

function Fontes() {
  const base = useBase();
  const { abrir } = useApp();
  const [q, setQ] = useState("");
  const fontes = base.fontes.filter((f) => combina(q, f.sigla, f.titulo, f.orgao, f.tipo));
  return (
    <>
      <Cabeca titulo="Fontes e auditoria" sub={`${base.fontes.length} documentos · base de ${dataBR(base.meta?.conferidoEm)}`} />
      <Busca valor={q} aoMudar={setQ} placeholder="Sigla, título ou autoria" rotulo="Buscar fonte" />
      {q ? null : (
        <Secao titulo="Como auditamos" icone="shield" tom="ind">
          <ul className="itens">
            {(base.auditoria ?? []).map((t) => (
              <li key={t}>
                <p>{t}</p>
              </li>
            ))}
          </ul>
        </Secao>
      )}
      {fontes.length ? (
        <Grupo titulo={q ? "Resultados" : "Documentos"} n={fontes.length} densos>
          {fontes.map((f) => {
            const ok = f.status === "verificado";
            return (
              <Cartao
                key={f.id}
                icone="file"
                tom={ok ? "ind" : "aviso"}
                titulo={f.sigla}
                sub={f.titulo}
                valor={<span className={`etq ${ok ? "bom" : "atencao"}`}>{ok ? "conferida" : "pendente"}</span>}
                aoTocar={() => abrir({ t: "ref", f: f.id })}
              />
            );
          })}
        </Grupo>
      ) : (
        <SemResultado termo={q} />
      )}
      {base.lacunas?.length && !q ? (
        <Secao titulo="O que a pesquisa não conseguiu confirmar" icone="info" tom="aviso" n={base.lacunas.length}>
          <p className="dica">Ficou fora do app em vez de entrar de memória.</p>
          <ul className="itens off">
            {base.lacunas.map((t) => (
              <li key={t}>
                <p>{t}</p>
              </li>
            ))}
          </ul>
        </Secao>
      ) : null}
    </>
  );
}
